import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import sha256 from 'crypto-js/sha256';
import CryptoJS from 'crypto-js';
import { useTheme, styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import { Grid, Stack, Typography, Link, useMediaQuery, Box, TextField } from '@mui/material';

// project imports
import AuthCardWrapper from 'components/Cards/AuthCardWrapper';
import logoImg from 'assets/images/raptor_tech_logo.png';
import ApiHelper from 'utils/apiHelper';
import { validatePassword } from 'utils/commonHelpers';

const AuthWrapper = styled('div')(({ theme }) => ({
    background: `linear-gradient(white, ${theme.palette.primary.main})`
}));

const ResetPassword = ({ title }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [queryParameters] = useSearchParams();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [lengthLimits, setLengthLimits] = useState({});
    const [isContainUpper, setIsContainUpper] = useState(true);
    const [isContainLower, setIsContainLower] = useState(true);
    const [isContainNumber, setIsContainNumber] = useState(true);
    const [spclChar, setSpclChar] = useState('!@#$%^&*()_+-={}[]|\\:;<>.?/');

    useEffect(() => {}, []);

    const getSecurityParams = async () => {
        const headers = {
            'Content-Type': 'application/json'
        };

        try {
            const res = await ApiHelper.post(
                '/api/auth/security_settings/',
                {
                    uid: queryParameters.get('uid')
                },
                headers,
                {},
                false
            );

            if (res.data && res.data.passwordSettings) {
                setLengthLimits({
                    usernameMaxLength: res.data.passwordSettings.usernameMaxLength || 120,
                    passwordMaxLength: res.data.passwordSettings.passwordMaxLength || 120,
                    passwordMinLength: res.data.passwordSettings.passwordMinLength || 8
                });
                setIsContainLower(res.data.passwordSettings.pwdLwrReq);
                setIsContainUpper(res.data.passwordSettings.pwdCapReq);
                setIsContainNumber(res.data.passwordSettings.pwdNumReq);
                setSpclChar(res.data.passwordSettings.SpclChar);
                return true;
            } else {
                return false;
            }
        } catch (err) {
            toast.error('Failed to fetch security settings:', err);
            return false;
        }
    };

    const handleSubmit = async () => {
        const isSecurityValid = await getSecurityParams();

        if (!isSecurityValid) {
            return;
        }

        if (password === '') {
            toast.warning('Password is required');
            return true;
        }

        const result = validatePassword(
            password,
            lengthLimits.passwordMinLength,
            lengthLimits.passwordMaxLength,
            isContainUpper,
            isContainLower,
            isContainNumber,
            spclChar
        );

        if (!result.isValid) {
            result.errors.forEach((error) => {
                toast.warning(error);
            });
            return true;
        }

        if (password !== confirmPassword) {
            toast.warning('Confirm password does not match');
            return true;
        }

        setLoading(true);

        ApiHelper.post(
            '/api/auth/create_password/',
            {
                uid: queryParameters.get('uid'),
                token: queryParameters.get('token'),
                password: sha256(password).toString(CryptoJS.enc.Hex)
            },
            {},
            {},
            false
        )
            .then(() => {
                toast.success('Successfully done');
                setLoading(false);
                navigate('/login');
            })
            .catch((err) => {
                if (err.response?.data?.message) {
                    toast.error(`Failed! ${err.response.data.message}`);
                } else {
                    toast.error('Failed to create the password');
                }
                setLoading(false);
            });
    };
    return (
        <AuthWrapper>
            <Grid container direction="column" justifyContent="flex-end" sx={{ minHeight: '100vh' }}>
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 'calc(100vh - 68px)' }}>
                        <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
                            <AuthCardWrapper>
                                <Grid container spacing={2} alignItems="center" justifyContent="center">
                                    <Grid item sx={{ mb: 3 }}>
                                        <Link href="/">
                                            <img src={logoImg} width="150" alt="" />
                                        </Link>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            direction={matchDownSM ? 'column-reverse' : 'row'}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Grid item>
                                                <Stack alignItems="center" justifyContent="center" spacing={1}>
                                                    <Typography
                                                        color={theme.palette.primary.main}
                                                        gutterBottom
                                                        variant={matchDownSM ? 'h3' : 'h2'}
                                                    >
                                                        WakeSMART Administration Login
                                                    </Typography>
                                                    <Typography variant="subtitle1">{title}</Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ mb: 2, mt: 2 }}>
                                            <TextField
                                                sx={{
                                                    width: '100%'
                                                }}
                                                type="password"
                                                label="Password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                            />
                                        </Box>
                                        <Box sx={{ mb: 2, mt: 2 }}>
                                            <TextField
                                                sx={{
                                                    width: '100%'
                                                }}
                                                type="password"
                                                inputProps={{
                                                    maxLength: lengthLimits.passwordMaxLength
                                                }}
                                                label="Confirm Password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                            />
                                        </Box>
                                        <Box sx={{ mb: 2, mt: 2 }}>
                                            <LoadingButton
                                                sx={{ width: '100%' }}
                                                onClick={handleSubmit}
                                                loading={loading}
                                                color="primary"
                                                inputProps={{
                                                    maxLength: lengthLimits.passwordMaxLength
                                                }}
                                                variant="contained"
                                            >
                                                <span>Submit</span>
                                            </LoadingButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AuthCardWrapper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </AuthWrapper>
    );
};

ResetPassword.propTypes = {
    title: PropTypes.string
};

export default ResetPassword;
